/**
 * Parse + light-validate scene.v1 documents for GM load.
 * Full JSON Schema lives in schema/; this is the runtime subset the party trusts.
 */

import { parseFogOps, type FogOp } from "./fog";
import {
  resolvePinVisibility,
  type PinVisibilityMode,
} from "./pinVisibility";
import {
  parseSceneWidgets,
  type SceneWidget,
} from "./widgets";

export type { FogOp } from "./fog";
export type { PinVisibilityMode } from "./pinVisibility";
export type { SceneWidget } from "./widgets";

export type SceneV1TokenBar = {
  current: number;
  max: number;
};

export type SceneV1Token = {
  id: string;
  name: string;
  artUrl: string;
  x: number;
  y: number;
  ownerKey?: string | null;
  /** Atlas isHidden — GM-only on the board. */
  gmOnly?: boolean;
  /** Diameter in map pixels (Atlas grid cell by default). */
  size?: number;
  /** Atlas token ring visibility. */
  showRing?: boolean;
  /** Atlas ring colour (CSS hex/rgb). */
  ringColor?: string | null;
  /** Atlas showNameplate. */
  showNameplate?: boolean;
  /** Atlas HP bar. */
  hp?: SceneV1TokenBar | null;
  /** Atlas stress bar. */
  stress?: SceneV1TokenBar | null;
  /** Per-token statblock note path. */
  notePath?: string | null;
};

/** Note pin on the board (S8+). Visibility extends gmOnly. */
export type SceneV1Pin = {
  id: string;
  x: number;
  y: number;
  label: string;
  notePath?: string | null;
  /** Atlas icon key (pin, lore, treasure, location, …). */
  icon?: string | null;
  gmOnly?: boolean;
  /** Just me / all players / one player. Default from gmOnly. */
  visibility?: PinVisibilityMode;
  /** Display name when visibility === "player". */
  visibleTo?: string | null;
  /** Optional inline markdown. Server prefers the room notes map when set. */
  content?: string | null;
};

export type SceneGrid = {
  enabled: boolean;
  visible: boolean;
  type?: "square" | "hex" | string;
  size: number;
  offsetX?: number;
  offsetY?: number;
  opacity?: number;
  color?: string;
  lineType?: string;
  lineWidth?: number;
};

export type SceneV1Document = {
  schemaVersion: "scene.v1";
  id: string;
  name: string;
  map: {
    url: string;
    width?: number;
    height?: number;
    grid?: SceneGrid | null;
  };
  tokens: SceneV1Token[];
  pins?: SceneV1Pin[];
  /** Atlas fog ops (paint/erase brush, lasso, rectangle). */
  fog?: FogOp[];
  /** Atlas Fear / clocks / counters HUD. */
  widgets?: SceneWidget[];
};

export const DEFAULT_MAP_WIDTH = 800;
export const DEFAULT_MAP_HEIGHT = 500;
/** Match Atlas default grid cell (tokens are typically 1 cell). */
export const TOKEN_SIZE = 70;
export const MAX_PINS = 64;

export type ParsedPinSeed = {
  id: string;
  x: number;
  y: number;
  label: string;
  notePath: string | null;
  icon: string | null;
  gmOnly: boolean;
  visibility: PinVisibilityMode;
  visibleTo: string | null;
  content: string | null;
};

export type ParsedSceneSeed = {
  id: string;
  name: string;
  map: { url: string; width: number; height: number; grid?: SceneGrid | null };
  tokens: Array<{
    id: string;
    name: string;
    artUrl: string;
    x: number;
    y: number;
    ownerKey: string | null;
    gmOnly: boolean;
    size: number;
    showRing: boolean;
    ringColor: string | null;
    showNameplate: boolean;
    hp: SceneV1TokenBar | null;
    stress: SceneV1TokenBar | null;
    notePath?: string | null;
  }>;
  pins: ParsedPinSeed[];
  fog: FogOp[];
  widgets: SceneWidget[];
};

function parsePins(
  raw: unknown,
  seenIds: Set<string>,
): { ok: true; pins: ParsedPinSeed[] } | { ok: false; error: string } {
  if (raw === undefined) {
    return { ok: true, pins: [] };
  }
  if (!Array.isArray(raw)) {
    return { ok: false, error: "pins must be an array" };
  }
  if (raw.length > MAX_PINS) {
    return { ok: false, error: `Too many pins (max ${MAX_PINS})` };
  }
  const pins: ParsedPinSeed[] = [];
  for (let i = 0; i < raw.length; i++) {
    const p = raw[i];
    if (typeof p !== "object" || p === null) {
      return { ok: false, error: `pins[${i}] must be an object` };
    }
    const pin = p as Record<string, unknown>;
    if (typeof pin.id !== "string" || !pin.id.trim()) {
      return { ok: false, error: `pins[${i}].id is required` };
    }
    const id = pin.id.trim();
    if (seenIds.has(id)) {
      return { ok: false, error: `Duplicate pin id: ${id}` };
    }
    seenIds.add(id);
    if (typeof pin.x !== "number" || !Number.isFinite(pin.x)) {
      return { ok: false, error: `pins[${i}].x must be a number` };
    }
    if (typeof pin.y !== "number" || !Number.isFinite(pin.y)) {
      return { ok: false, error: `pins[${i}].y must be a number` };
    }
    const label =
      typeof pin.label === "string" && pin.label.trim()
        ? pin.label.trim()
        : id;
    let notePath: string | null = null;
    if (pin.notePath !== undefined && pin.notePath !== null) {
      if (typeof pin.notePath !== "string") {
        return { ok: false, error: `pins[${i}].notePath must be string or null` };
      }
      notePath = pin.notePath.trim() || null;
    }
    let content: string | null = null;
    if (pin.content !== undefined && pin.content !== null) {
      if (typeof pin.content !== "string") {
        return { ok: false, error: `pins[${i}].content must be string or null` };
      }
      content = pin.content;
    }
    const vis = resolvePinVisibility(pin);
    const icon =
      typeof pin.icon === "string" && pin.icon.trim()
        ? pin.icon.trim().slice(0, 32)
        : null;
    pins.push({
      id,
      x: pin.x,
      y: pin.y,
      label: label.slice(0, 120),
      notePath,
      icon,
      gmOnly: vis.gmOnly,
      visibility: vis.visibility,
      visibleTo: vis.visibleTo,
      content,
    });
  }
  return { ok: true, pins };
}

function parseTokenBar(
  raw: unknown,
  fallbackMax: number,
): SceneV1TokenBar | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return {
      current: Math.max(0, raw),
      max: Math.max(1, fallbackMax),
    };
  }
  if (typeof raw !== "object") return null;
  const bag = raw as Record<string, unknown>;
  if (
    typeof bag.current !== "number" ||
    !Number.isFinite(bag.current) ||
    typeof bag.max !== "number" ||
    !Number.isFinite(bag.max) ||
    bag.max <= 0
  ) {
    return null;
  }
  return {
    current: Math.max(0, bag.current),
    max: bag.max,
  };
}

export function parseSceneV1(raw: unknown):
  | { ok: true; scene: ParsedSceneSeed }
  | { ok: false; error: string } {
  if (typeof raw !== "object" || raw === null) {
    return { ok: false, error: "Scene must be a JSON object" };
  }
  const doc = raw as Record<string, unknown>;

  if (doc.schemaVersion !== "scene.v1") {
    return { ok: false, error: 'schemaVersion must be "scene.v1"' };
  }
  if (typeof doc.id !== "string" || !doc.id.trim()) {
    return { ok: false, error: "id is required" };
  }
  if (typeof doc.name !== "string" || !doc.name.trim()) {
    return { ok: false, error: "name is required" };
  }
  if (typeof doc.map !== "object" || doc.map === null) {
    return { ok: false, error: "map is required" };
  }
  const map = doc.map as Record<string, unknown>;
  if (typeof map.url !== "string" || !map.url.trim()) {
    return { ok: false, error: "map.url is required" };
  }
  const width =
    typeof map.width === "number" && Number.isFinite(map.width) && map.width > 0
      ? map.width
      : DEFAULT_MAP_WIDTH;
  const height =
    typeof map.height === "number" &&
    Number.isFinite(map.height) &&
    map.height > 0
      ? map.height
      : DEFAULT_MAP_HEIGHT;

  if (!Array.isArray(doc.tokens)) {
    return { ok: false, error: "tokens must be an array" };
  }
  if (doc.tokens.length > 64) {
    return { ok: false, error: "Too many tokens (max 64)" };
  }

  const seen = new Set<string>();
  const tokens: ParsedSceneSeed["tokens"] = [];
  for (let i = 0; i < doc.tokens.length; i++) {
    const t = doc.tokens[i];
    if (typeof t !== "object" || t === null) {
      return { ok: false, error: `tokens[${i}] must be an object` };
    }
    const tok = t as Record<string, unknown>;
    if (typeof tok.id !== "string" || !tok.id.trim()) {
      return { ok: false, error: `tokens[${i}].id is required` };
    }
    if (seen.has(tok.id)) {
      return { ok: false, error: `Duplicate token id: ${tok.id}` };
    }
    seen.add(tok.id);
    if (typeof tok.name !== "string" || !tok.name.trim()) {
      return { ok: false, error: `tokens[${i}].name is required` };
    }
    if (typeof tok.artUrl !== "string" || !tok.artUrl.trim()) {
      return { ok: false, error: `tokens[${i}].artUrl is required` };
    }
    if (typeof tok.x !== "number" || !Number.isFinite(tok.x)) {
      return { ok: false, error: `tokens[${i}].x must be a number` };
    }
    if (typeof tok.y !== "number" || !Number.isFinite(tok.y)) {
      return { ok: false, error: `tokens[${i}].y must be a number` };
    }
    let ownerKey: string | null = null;
    if (tok.ownerKey !== undefined && tok.ownerKey !== null) {
      if (typeof tok.ownerKey !== "string") {
        return { ok: false, error: `tokens[${i}].ownerKey must be string or null` };
      }
      ownerKey = tok.ownerKey.trim() || null;
    }
    let size = TOKEN_SIZE;
    if (typeof tok.size === "number" && Number.isFinite(tok.size) && tok.size > 0) {
      size = Math.min(1024, Math.max(48, tok.size));
    }
    const ringColor =
      typeof tok.ringColor === "string" && tok.ringColor.trim()
        ? tok.ringColor.trim().slice(0, 32)
        : null;
    const showRing =
      tok.showRing === true ||
      (tok.showRing !== false && ringColor !== null);
    const showNameplate = tok.showNameplate === true;
    const hp = parseTokenBar(tok.hp, 100);
    let stress = parseTokenBar(tok.stress, 10);
    if (
      stress === null &&
      typeof tok.maxStress === "number" &&
      Number.isFinite(tok.maxStress) &&
      typeof tok.stress === "number" &&
      Number.isFinite(tok.stress)
    ) {
      stress = {
        current: Math.max(0, tok.stress),
        max: Math.max(1, tok.maxStress),
      };
    }
    tokens.push({
      id: tok.id.trim(),
      name: tok.name.trim(),
      artUrl: tok.artUrl.trim(),
      x: tok.x,
      y: tok.y,
      ownerKey,
      gmOnly: tok.gmOnly === true,
      size,
      showRing,
      ringColor,
      showNameplate,
      hp,
      stress,
      notePath:
        typeof tok.notePath === "string" && tok.notePath.trim()
          ? tok.notePath.trim()
          : null,
    });
  }

  const pinsParsed = parsePins(doc.pins, seen);
  if (!pinsParsed.ok) return pinsParsed;

  const fogParsed = parseFogOps(doc.fog);
  if (!fogParsed.ok) return fogParsed;

  const widgetsParsed = parseSceneWidgets(doc.widgets);
  if (!widgetsParsed.ok) return widgetsParsed;

  let grid: SceneGrid | null = null;
  if (typeof map.grid === "object" && map.grid !== null) {
    const g = map.grid as Record<string, unknown>;
    const size = typeof g.size === "number" && Number.isFinite(g.size) && g.size > 0 ? g.size : 70;
    grid = {
      enabled: g.enabled !== false,
      visible: g.visible !== false,
      type: typeof g.type === "string" ? g.type : "square",
      size,
      offsetX: typeof g.offsetX === "number" && Number.isFinite(g.offsetX) ? g.offsetX : 0,
      offsetY: typeof g.offsetY === "number" && Number.isFinite(g.offsetY) ? g.offsetY : 0,
      opacity: typeof g.opacity === "number" && Number.isFinite(g.opacity) ? g.opacity : 0.4,
      color: typeof g.color === "string" ? g.color : "rgba(255, 255, 255, 0.4)",
      lineType: typeof g.lineType === "string" ? g.lineType : "solid",
      lineWidth: typeof g.lineWidth === "number" && Number.isFinite(g.lineWidth) ? g.lineWidth : 1,
    };
  }

  return {
    ok: true,
    scene: {
      id: doc.id.trim(),
      name: doc.name.trim(),
      map: { url: map.url.trim(), width, height, ...(grid ? { grid } : {}) },
      tokens,
      pins: pinsParsed.pins,
      fog: fogParsed.fog,
      widgets: widgetsParsed.widgets,
    },
  };
}
